import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { enqueueSnackbar } from 'notistack';

import Input from '@/components/input/Input';
import Button from '@/components/input/Button';
import Spinner from '@/components/dataDisplay/Spinner';
import { routes } from '@/constants/routes';
import { Content } from '@/services/contents/content.vm';

import DeleteButton from './DeleteButton';
import useLearningContents from '@/context/contents/hook';
import { contentsService } from '@/services/contents';
import ContentTypeSelector from './ContentTypeSelector';
import { Stage } from '@/services/stages/stage.vm';
import { Route } from '@/services/routes/route.vm';
import OverviewEditor from './OverviewEditor';

interface CreateEditContentFormProps {
  routeId?: Route['id'];
  stageId?: Stage['id'];
  contentId?: Content['id'];
  onSubmit?: () => void;
  onCancel?: () => void;
  isStageContent?: boolean;
}

export const DEFAULT_POINTS = 5;
export const DEFAULT_DURATION = 60;

const CreateEditContentForm: React.FC<CreateEditContentFormProps> = ({
  contentId,
  routeId,
  stageId,
  onSubmit,
  onCancel,
  isStageContent = false,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { addLearningContent, updateLearningContent, addStageContent, updateStageContent } =
    useLearningContents();

  const [title, setTitle] = useState('');
  const [type, setType] = useState<Content['type']>('READING');
  const [content, setContent] = useState('');
  const [points, setPoints] = useState<number | string>(DEFAULT_POINTS);
  const [subtitles, setSubtitles] = useState<string | null>(null);
  const [subtitlesError, setSubtitlesError] = useState<boolean>(false);
  const [duration, setDuration] = useState<number | string>(DEFAULT_DURATION);
  const [file, setFile] = useState<File | null | string>(null);
  const [quizData, setQuizData] = useState<string | null>(null);
  const [overviewData, setOverviewData] = useState<string | null>(null);
  const [analysisPrompt, setAnalysisPrompt] = useState<string | null>(null);

  const handleCancel: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    if (onCancel) {
      onCancel();
    } else {
      navigate(routes.admin.content.root);
    }
  };

  const handleReset = () => {
    setTitle('');
    setPoints(DEFAULT_POINTS);
    setDuration(DEFAULT_DURATION);
    setType('READING');
    setContent('');
    setFile(null);
    setQuizData(null);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    // const error = false;
    const submitter = (e.nativeEvent as Event & { submitter: HTMLElement }).submitter as HTMLButtonElement;

    const form = e.currentTarget;
    const formData = new FormData();

    formData.append('title', title);
    formData.append('type', type);
    formData.append('content', content);
    if (Number(points) > 0) {
      formData.append('points', points.toString());
    }
    if (Number(duration) > 0) {
      formData.append('duration', duration.toString());
    }
    if (quizData) {
      formData.append('quizData', quizData);
    }
    if (overviewData) {
      formData.append('overviewData', overviewData);
    }
    if (analysisPrompt) {
      formData.append('analysisPrompt', analysisPrompt);
    }
    if (subtitles) {
      setSubtitlesError(false);
      if (subtitles.length === 0) {
        formData.append('subtitles', '[]');
      } else {
        try {
          const parsedSubtitles = typeof subtitles === 'string' && JSON.parse(subtitles);
          if (
            Array.isArray(parsedSubtitles) &&
            parsedSubtitles.every(
              (item) =>
                typeof item.text === 'string' &&
                typeof item.start === 'number' &&
                typeof item.end === 'number',
            )
          ) {
            formData.append('subtitles', JSON.stringify(parsedSubtitles));
          } else {
            setSubtitlesError(true);
            setLoading(false);
            return;
          }
        } catch (err) {
          setSubtitlesError(true);
          setLoading(false);
          return;
        }
      }
    }
    if (file && typeof file !== 'string') {
      formData.append('file', file);
    }
    if (!isStageContent && routeId) {
      formData.append('routeId', routeId);
    }
    if (isStageContent && stageId) {
      formData.append('stageId', stageId);
    }

    const service = () =>
      contentId
        ? contentsService.updateContent(contentId, formData)
        : contentsService.createContent(formData);

    service()
      .then((content) => {
        if (!routeId) return;
        if (contentId) {
          if (isStageContent && stageId) {
            updateStageContent(routeId, stageId, contentId, content);
          } else {
            updateLearningContent(routeId, contentId, content);
          }
        } else {
          if (isStageContent && stageId) {
            addStageContent(routeId, stageId, content);
          } else {
            addLearningContent(content);
          }
        }
        if (onSubmit) {
          onSubmit();
        } else if (submitter.name === 'save') {
          navigate(-1);
        } else if (submitter.name === 'saveAndAddAnother') {
          if (!contentId) {
            handleReset();
            form.reset();
          }
        }
        const toasterMessage = contentId
          ? 'Contenido actualizado correctamente'
          : 'Contenido creado correctamente';
        enqueueSnackbar(toasterMessage, {
          variant: 'success',
        });
      })
      .catch((error) => {
        enqueueSnackbar('Error al crear o editar contenido', {
          variant: 'error',
        });
        console.error('Error creating or editing content', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (contentId) {
      contentsService.getContentbyId(contentId).then((content) => {
        setTitle(content.title);
        setType(content.type);
        setContent(content.content ?? '');
        setPoints(content.points ?? DEFAULT_POINTS);
        setSubtitles(content.subtitles ? JSON.stringify(content.subtitles) : null);
        setDuration(content.duration ?? DEFAULT_DURATION);
        setFile(content.src ?? null);
        setQuizData(content.quizData ?? null);
        setOverviewData(content.overviewData ?? null);
        setAnalysisPrompt(content.analysisPrompt ?? null);
      });
    }
  }, [contentId]);

  return (
    <form
      style={{ display: 'flex', flexDirection: 'column', gap: 16, overflowX: 'visible' }}
      onSubmit={handleSubmit}
    >
      <Input
        name="title"
        required
        type="text"
        placeholder="Título"
        value={title}
        label="Título"
        helperText="Este título se mostrará a todos los usuarios y servirá para identificar el contenido."
        onChange={(e) => setTitle(e.target.value)}
      />
      <Input
        name="points"
        required
        type="number"
        placeholder="Puntos"
        value={points}
        label="Puntos"
        helperText="Los puntos serviran para incrementar el gamification y se sumarán a la puntuación de cada usuario cuando completa el contenido."
        onChange={(e) => {
          const value = e.target.value;
          setPoints(value === '' ? '' : Number(value));
        }}
      />
      <Input
        name="duration"
        required
        type="number"
        placeholder="Duración"
        value={duration ?? 60}
        label="Duración"
        helperText="La duración del contenido en segundos."
        onChange={(e) => {
          const value = e.target.value;
          setDuration(value === '' ? '' : Number(value));
        }}
      />
      {/* {!routeId && (
        <Select
          name="routeId"
          required
          value={selectedRouteId}
          label="Ruta ligada"
          helperText="Selecciona la ruta a la que se vinculará este contenido."
          onChange={(e) => setSelectedRouteId(e.target.value as string)}
        >
          <option value="">Seleccionar ruta</option>
          {routesList?.map((route) => (
            <option key={route.id} value={route.id}>
              {route.title}
            </option>
          ))}
        </Select>
      )} */}
      <OverviewEditor overviewData={overviewData} setOverviewData={setOverviewData} />
      <ContentTypeSelector
        type={type}
        setType={setType}
        file={file}
        setFile={setFile}
        content={content}
        setContent={setContent}
        subtitles={subtitles}
        setSubtitles={setSubtitles}
        subtitlesError={subtitlesError}
        quizData={quizData}
        setQuizData={setQuizData}
        setDuration={setDuration}
        analysisPrompt={analysisPrompt}
        setAnalysisPrompt={setAnalysisPrompt}
      />
      <div
        style={{
          display: 'flex',
          gap: 16,
          justifyContent: 'space-between',
          ...(loading && { opacity: 0.5 }),
        }}
      >
        {!contentId ? (
          <Button type="button" variant="danger" onClick={handleCancel} disabled={loading}>
            Cancelar
          </Button>
        ) : (
          <DeleteButton
            routeId={routeId}
            contentId={contentId}
            loading={loading}
            isStageContent={isStageContent}
          />
        )}
        <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
          {!routeId && (
            <Button type="submit" name="saveAndAddAnother" variant="outline" disabled={loading}>
              {contentId ? 'Guardar y seguir editando' : 'Guardar y añadir otro'}
            </Button>
          )}
          <Button type="submit" name="save" disabled={loading}>
            {loading && <Spinner style={{ marginRight: 8 }} />}
            Guardar
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CreateEditContentForm;
